import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useAuth } from "@/hooks/useAuth";
import { useOperationLogs } from "./useOperationLogs";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { logDedupError, isOnline } from "@/lib/error-dedup";
import { getBrasiliaDate } from "@/lib/date-utils";

interface AutoCloseSettings {
  enabled: boolean;
  hours: number;
  requireManualClose: boolean;
}

export interface ManualCloseStatus {
  shouldBlock: boolean;
  reason: 'period_expired' | 'none';
  hasCashRegisterOpen: boolean;
  periodDate: string | null;
  hoursSinceLastActivity: number;
  hasOpenConsumptions: boolean;
  openConsumptionsCount: number;
  canTransferPending: boolean;
}

interface AutoCloseResult {
  wasAutoClosed: boolean;
  requiresManualIntervention?: boolean;
  oldPeriodDate?: string;
  newPeriodDate?: string;
}

export function useAutoClosePeriod() {
  const { activeRestaurant } = useRestaurantContext();
  const { user } = useAuth();
  const { logOperation } = useOperationLogs();
  const [settings, setSettings] = useState<AutoCloseSettings>({ enabled: true, hours: 6, requireManualClose: false });
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch settings from restaurant
  const fetchSettings = useCallback(async () => {
    if (!activeRestaurant?.id) {
      setIsLoading(false);
      return;
    }

    if (!isOnline()) {
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("restaurants")
        .select("auto_close_period_enabled, auto_close_period_hours, require_period_close_after_inactivity")
        .eq("id", activeRestaurant.id)
        .single();

      if (error) throw error;

      setSettings({
        enabled: data.auto_close_period_enabled ?? true,
        hours: data.auto_close_period_hours ?? 6,
        requireManualClose: data.require_period_close_after_inactivity ?? false,
      });
    } catch (error) {
      logDedupError("AutoClosePeriod.fetchSettings", error);
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant?.id]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Update settings
  const updateSettings = useCallback(async (newSettings: Partial<AutoCloseSettings>) => {
    if (!activeRestaurant) return false;

    try {
      const { error } = await supabase
        .from("restaurants")
        .update({
          auto_close_period_enabled: newSettings.enabled ?? settings.enabled,
          auto_close_period_hours: newSettings.hours ?? settings.hours,
          require_period_close_after_inactivity: newSettings.requireManualClose ?? settings.requireManualClose,
        })
        .eq("id", activeRestaurant.id);

      if (error) throw error;

      setSettings(prev => ({ ...prev, ...newSettings }));
      toast.success("Configurações salvas!");
      return true;
    } catch (error) {
      console.error("Error updating auto-close settings:", error);
      toast.error("Erro ao salvar configurações");
      return false;
    }
  }, [activeRestaurant, settings]);

  // Check and auto-close period if needed
  const checkAndAutoClose = useCallback(async (): Promise<AutoCloseResult> => {
    if (!activeRestaurant?.id || !user || !settings.enabled || settings.requireManualClose) {
      return { wasAutoClosed: false };
    }

    if (!isOnline()) {
      return { wasAutoClosed: false };
    }

    setIsProcessing(true);

    try {
      const { data: currentPeriod, error: periodError } = await supabase
        .from("operational_periods")
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("status", "open")
        .order("opened_at", { ascending: false })
        .maybeSingle();

      if (periodError) throw periodError;
      if (!currentPeriod) return { wasAutoClosed: false };

      const today = getBrasiliaDate();
      const periodDate = currentPeriod.reference_date;

      if (periodDate === today) return { wasAutoClosed: false };

      // Period is from a previous day — check inactivity
      const { data: lastOrder } = await supabase
        .from("orders")
        .select("created_at")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("period_id", currentPeriod.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      const lastActivity = lastOrder?.created_at
        ? new Date(lastOrder.created_at)
        : new Date(currentPeriod.opened_at);

      const now = new Date();
      const hoursSinceLastActivity = (now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60);

      if (hoursSinceLastActivity < settings.hours) return { wasAutoClosed: false };

      // Cannot auto-close if registers are open
      const { data: openRegisters } = await supabase
        .from("cash_registers")
        .select("id")
        .eq("period_id", currentPeriod.id)
        .eq("status", "open");

      if (openRegisters && openRegisters.length > 0) {
        console.log("[useAutoClosePeriod] Cannot auto-close: open cash registers exist — requiring manual intervention");
        return { wasAutoClosed: false, requiresManualIntervention: true };
      }

      console.log(`[useAutoClosePeriod] Auto-closing period ${periodDate} due to ${hoursSinceLastActivity.toFixed(1)}h inactivity`);

      // Close active command entries
      const { data: activeEntries } = await supabase
        .from("command_entries")
        .select("id, command_id")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("status", "active");

      if (activeEntries && activeEntries.length > 0) {
        await supabase
          .from("command_entries")
          .update({
            status: "closed",
            consumed_amount: 0,
            closed_at: new Date().toISOString(),
          })
          .eq("restaurant_id", activeRestaurant.id)
          .eq("status", "active");

        const commandIds = activeEntries.map(e => e.command_id).filter(Boolean) as string[];
        if (commandIds.length > 0) {
          await supabase
            .from("commands")
            .update({ status: "available", updated_at: new Date().toISOString() })
            .in("id", commandIds);
        }
      }

      // Calculate totals
      const { data: periodOrders } = await supabase
        .from("orders")
        .select("total")
        .eq("period_id", currentPeriod.id)
        .neq("status", "cancelled");

      const totalSales = periodOrders?.reduce((sum, order) => sum + Number(order.total), 0) || 0;
      const totalOrders = periodOrders?.length || 0;

      const { error: closeError } = await supabase
        .from("operational_periods")
        .update({
          status: "closed",
          closed_at: new Date().toISOString(),
          closed_by: user.id,
          notes: `Fechamento automático por inatividade (${hoursSinceLastActivity.toFixed(1)}h sem movimentação)`,
          total_sales: totalSales,
          total_orders: totalOrders,
        })
        .eq("id", currentPeriod.id);

      if (closeError) throw closeError;

      await logOperation("auto_close", "operational_period", currentPeriod.id, {
        reference_date: periodDate,
        hours_inactive: hoursSinceLastActivity.toFixed(1),
        total_sales: totalSales,
        total_orders: totalOrders,
      });

      // Open new period for today
      const { data: newPeriod, error: openError } = await supabase
        .from("operational_periods")
        .insert({
          restaurant_id: activeRestaurant.id,
          reference_date: today,
          opened_by: user.id,
          status: "open",
        })
        .select()
        .single();

      if (openError) throw openError;

      await logOperation("open", "operational_period", newPeriod.id, {
        reference_date: today,
        auto_opened_after_auto_close: true,
      });

      const formattedOldDate = format(new Date(periodDate + "T12:00:00"), "dd/MM", { locale: ptBR });
      const formattedNewDate = format(new Date(today + "T12:00:00"), "dd/MM", { locale: ptBR });

      toast.info(`Período do dia ${formattedOldDate} foi fechado automaticamente`, {
        description: `Novo período aberto para hoje (${formattedNewDate})`,
        duration: 8000,
      });

      return {
        wasAutoClosed: true,
        oldPeriodDate: periodDate,
        newPeriodDate: today,
      };
    } catch (error) {
      console.error("Error in auto-close check:", error);
      return { wasAutoClosed: false };
    } finally {
      setIsProcessing(false);
    }
  }, [activeRestaurant, user, settings, logOperation]);

  // Check if manual close is required (for PDV overlay/banner)
  const checkRequireManualClose = useCallback(async (force = false): Promise<ManualCloseStatus> => {
    const defaultResult: ManualCloseStatus = {
      shouldBlock: false,
      reason: 'none',
      hasCashRegisterOpen: false,
      periodDate: null,
      hoursSinceLastActivity: 0,
      hasOpenConsumptions: false,
      openConsumptionsCount: 0,
      canTransferPending: false,
    };

    if (!activeRestaurant?.id || (!settings.requireManualClose && !force)) {
      return defaultResult;
    }

    if (!isOnline()) {
      return defaultResult;
    }

    try {
      // Get current open period
      const { data: currentPeriod, error: periodError } = await supabase
        .from("operational_periods")
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("status", "open")
        .order("opened_at", { ascending: false })
        .maybeSingle();

      if (periodError) throw periodError;
      if (!currentPeriod) return defaultResult;

      const today = getBrasiliaDate();
      const periodDate = currentPeriod.reference_date;

      // If period is from today (Brasilia), nothing to do
      if (periodDate === today) return defaultResult;

      // Period is from a previous day — check inactivity
      const { data: lastOrder } = await supabase
        .from("orders")
        .select("created_at")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("period_id", currentPeriod.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      const lastActivity = lastOrder?.created_at
        ? new Date(lastOrder.created_at)
        : new Date(currentPeriod.opened_at);

      const now = new Date();
      const hoursSinceLastActivity = (now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60);

      if (hoursSinceLastActivity < settings.hours) return defaultResult;

      // Check for open cash registers
      const { data: openRegisters } = await supabase
        .from("cash_registers")
        .select("id")
        .eq("period_id", currentPeriod.id)
        .eq("status", "open");

      // Check for open consumptions (pending/preparing orders)
      const { data: openOrders } = await supabase
        .from("orders")
        .select("id")
        .eq("restaurant_id", activeRestaurant.id)
        .in("status", ["pending", "preparing"]);

      const openConsumptionsCount = openOrders?.length ?? 0;

      // Check transfer_pending_consumption flag
      const { data: restaurant } = await supabase
        .from("restaurants")
        .select("transfer_pending_consumption")
        .eq("id", activeRestaurant.id)
        .single();

      const canTransferPending = (restaurant as any)?.transfer_pending_consumption ?? false;

      return {
        shouldBlock: true,
        reason: 'period_expired',
        hasCashRegisterOpen: (openRegisters?.length ?? 0) > 0,
        periodDate,
        hoursSinceLastActivity,
        hasOpenConsumptions: openConsumptionsCount > 0,
        openConsumptionsCount,
        canTransferPending,
      };
    } catch (error) {
      console.error("Error checking manual close requirement:", error);
      return defaultResult;
    }
  }, [activeRestaurant?.id, settings.requireManualClose, settings.hours]);

  return {
    settings,
    isLoading,
    isProcessing,
    updateSettings,
    checkAndAutoClose,
    checkRequireManualClose,
    refetchSettings: fetchSettings,
  };
}
